import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AppLayout from '../../components/layout/AppLayout'
import Alert from '../../components/ui/Alert'
import { supabase } from '../../lib/supabase'
import { useStockMovements } from '../../hooks/useStockMovements'
import { useCadastroRecebimentoStatus } from '../../hooks/useCadastroRecebimentoStatus'
import { parseNfeXml, type NfeParsed, type NfeItem } from '../../lib/nfeXmlParser'
import type { Product, StockMovementType, Supplier } from '../../types'

type SubTab = 'recebimento' | 'cadastro' | 'importar-xml'

const SUB_TABS: { key: SubTab; label: string }[] = [
  { key: 'recebimento', label: 'Recebimento' },
  { key: 'cadastro',    label: 'Cadastro de Produto' },
  { key: 'importar-xml', label: 'Importar XML NFe' },
]

const TIPOS: { value: StockMovementType; label: string }[] = [
  { value: 'ENTRADA', label: 'Entrada' },
  { value: 'SAIDA',   label: 'Saída' },
  { value: 'AJUSTE',  label: 'Ajuste de saldo' },
]

const fmtBRL = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDateTime = (iso: string) => {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export default function CadastroRecebimentoPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { loading, enabled, hasV3 } = useCadastroRecebimentoStatus()

  const requested = (searchParams.get('tab') as SubTab) || 'recebimento'
  const subTab: SubTab = SUB_TABS.some(t => t.key === requested) ? requested : 'recebimento'
  const setSubTab = (k: SubTab) => setSearchParams({ tab: k }, { replace: true })

  if (loading) {
    return (
      <AppLayout title="Cadastro e Recebimento">
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    )
  }

  if (hasV3) {
    return (
      <AppLayout title="Cadastro e Recebimento">
        <div className="w-full max-w-2xl rounded-lg border border-sky-300 bg-sky-100 p-4 text-sm text-sky-800 dark:bg-sky-500/10 dark:border-sky-500/30 dark:text-sky-300">
          <p className="font-semibold mb-1">Recurso indisponivel</p>
          <p>
            Esta empresa esta integrada ao <strong>Tiny v3</strong>. O Cadastro e Recebimento
            so esta disponivel para empresas sem ERP integrado — para evitar duplicidade
            de cadastro e divergencia de estoque.
          </p>
          <p className="mt-2">
            Use o Tiny para cadastrar produtos e registrar entradas. O sync automatico
            traz os dados para o BX.
          </p>
        </div>
      </AppLayout>
    )
  }

  if (!enabled) {
    return (
      <AppLayout title="Cadastro e Recebimento">
        <div className="w-full max-w-2xl rounded-lg border border-sky-300 bg-sky-100 p-4 text-sm text-sky-800 dark:bg-sky-500/10 dark:border-sky-500/30 dark:text-sky-300">
          Este recurso esta desativado para a sua empresa. Um administrador pode ativar em <strong>Configuracoes &rarr; ERP</strong>.
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Cadastro e Recebimento">
      <div className="w-full">
        <div className="flex gap-1 border-b border-line mb-6 overflow-x-auto">
          {SUB_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setSubTab(t.key)}
              className={`text-sm sm:text-base px-3 sm:px-5 py-2.5 border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                subTab === t.key
                  ? 'border-brand-500 text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {subTab === 'recebimento' && <RecebimentoTab />}
        {subTab === 'cadastro' && <CadastroProdutoTab />}
        {subTab === 'importar-xml' && <ImportarXmlTab />}
      </div>
    </AppLayout>
  )
}

// =====================================================================
// Aba: RECEBIMENTO (leitor de codigo de barras)
// =====================================================================

function RecebimentoTab() {
  const { movements, loading, error, createMovement, findProductByBarcode, reactivateProduct } = useStockMovements(20)
  const [, setSearchParams] = useSearchParams()
  const barcodeRef = useRef<HTMLInputElement>(null)
  const [barcode, setBarcode] = useState('')
  const [product, setProduct] = useState<Product | null>(null)
  const [notFoundCode, setNotFoundCode] = useState<string | null>(null)
  const [tipo, setTipo] = useState<StockMovementType>('ENTRADA')
  const [quantidade, setQuantidade] = useState('1')
  const [valorUnit, setValorUnit] = useState('')
  const [numeroNota, setNumeroNota] = useState('')
  const [serieNota, setSerieNota] = useState('')
  const [chaveNfe, setChaveNfe] = useState('')
  const [fornecedorNome, setFornecedorNome] = useState('')
  const [fornecedorCnpj, setFornecedorCnpj] = useState('')
  const [observacao, setObservacao] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [msg, setMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)

  useEffect(() => { barcodeRef.current?.focus() }, [])

  async function lookupBarcode() {
    const code = barcode.trim()
    if (!code) { setProduct(null); setNotFoundCode(null); return }
    const p = await findProductByBarcode(code)
    if (!p) {
      setProduct(null)
      setNotFoundCode(code)
      setMsg(null)
      return
    }
    setProduct(p); setNotFoundCode(null)
    setMsg(null)
    if (p.cost && !valorUnit) setValorUnit(String(p.cost))
  }

  function goToCadastroWithBarcode() {
    if (!notFoundCode) return
    setSearchParams({ tab: 'cadastro', barcode: notFoundCode }, { replace: false })
  }

  async function handleReactivate() {
    if (!product) return
    const res = await reactivateProduct(product.id)
    if (!res.ok) { setMsg({ type: 'error', text: res.error ?? 'Falha ao reativar.' }); return }
    setProduct({ ...product, is_active: true })
    setMsg({ type: 'success', text: `Produto ${product.sku} reativado.` })
  }

  function resetForm(keepFocus = true) {
    setBarcode('')
    setProduct(null)
    setNotFoundCode(null)
    setQuantidade('1')
    setValorUnit('')
    setNumeroNota('')
    setSerieNota('')
    setChaveNfe('')
    setFornecedorNome('')
    setFornecedorCnpj('')
    setObservacao('')
    if (keepFocus) setTimeout(() => barcodeRef.current?.focus(), 50)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!product) { setMsg({ type: 'error', text: 'Bipe um codigo de barras valido primeiro.' }); return }
    const qty = Number(quantidade.replace(',', '.'))
    if (!Number.isFinite(qty) || qty < 0) { setMsg({ type: 'error', text: 'Quantidade invalida.' }); return }
    const val = Number(valorUnit.replace(',', '.')) || 0
    setSubmitting(true)
    const res = await createMovement({
      product_id: product.id,
      tipo,
      quantidade: qty,
      valor_unitario: val,
      numero_nota: numeroNota || null,
      serie_nota: serieNota || null,
      chave_nfe: chaveNfe || null,
      fornecedor_nome: fornecedorNome || null,
      fornecedor_cnpj: fornecedorCnpj || null,
      observacao: observacao || null,
    })
    setSubmitting(false)
    if (!res.ok) { setMsg({ type: 'error', text: res.error ?? 'Falha ao lancar movimento.' }); return }
    setMsg({ type: 'success', text: `Movimento ${tipo} de ${qty} ${product.unit ?? 'UN'} registrado.` })
    resetForm()
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="bg-surface border border-line rounded-xl p-6">
        <h2 className="text-lg font-semibold text-ink mb-1">Lancamento</h2>
        <p className="text-sm text-ink-faint mb-4">Bipe o codigo de barras (ou digite e tecle Enter) — busca automatica do produto, registra entrada/saida/ajuste e atualiza o saldo.</p>

        {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} /></div>}

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[260px]">
              <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">Codigo de barras</label>
              <input
                ref={barcodeRef}
                value={barcode}
                onChange={e => setBarcode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupBarcode() } }}
                onBlur={lookupBarcode}
                placeholder="Bipe ou digite + Enter"
                className="w-full bg-paper border border-line rounded px-3 py-2 text-ink text-sm"
              />
            </div>
            <div className="w-44">
              <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">Tipo</label>
              <select value={tipo} onChange={e => setTipo(e.target.value as StockMovementType)}
                className="w-full bg-paper border border-line rounded px-2 py-2 text-ink text-sm">
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="w-32">
              <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">Quantidade</label>
              <input
                type="text" inputMode="decimal"
                value={quantidade} onChange={e => setQuantidade(e.target.value)}
                className="w-full bg-paper border border-line rounded px-2 py-2 text-ink text-sm text-right"
              />
            </div>
            <div className="w-36">
              <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">Valor unit. (R$)</label>
              <input
                type="text" inputMode="decimal"
                value={valorUnit} onChange={e => setValorUnit(e.target.value)}
                className="w-full bg-paper border border-line rounded px-2 py-2 text-ink text-sm text-right"
              />
            </div>
          </div>

          {product && (
            <div className={`rounded-lg border p-3 text-sm flex flex-wrap items-start justify-between gap-3 ${
              product.is_active === false
                ? 'border-warn/40 bg-warn/10'
                : 'border-line bg-sunken'
            }`}>
              <div className="flex-1 min-w-[260px]">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-ink font-semibold">{product.name}</p>
                  {product.is_active === false && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-warn/15 text-[#9a5a28] border border-warn/40 dark:bg-warn/20 dark:text-amber-300">INATIVO</span>
                  )}
                </div>
                <p className="text-ink-muted mt-1">
                  SKU {product.sku} · Saldo atual {product.current_stock} {product.unit ?? 'UN'}
                  {product.barcode && <> · GTIN {product.barcode}</>}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.is_active === false && (
                  <button type="button" onClick={handleReactivate}
                    className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded-lg">
                    Reativar produto
                  </button>
                )}
              </div>
            </div>
          )}

          {notFoundCode && (
            <div className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-[260px]">
                <p className="text-danger">
                  <strong>Codigo nao encontrado:</strong> "{notFoundCode}" nao esta cadastrado em nenhum produto (busca por GTIN/EAN).
                </p>
                <p className="text-danger/80 text-xs mt-1">
                  Va em <strong>Cadastro de Produto</strong>, busque o produto pelo SKU ou nome e preencha este codigo no campo "Codigo de barras". Depois volte aqui para lancar.
                </p>
              </div>
              <button type="button" onClick={goToCadastroWithBarcode}
                className="px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium rounded-lg">
                Ir para Cadastro
              </button>
            </div>
          )}

          <details className="bg-sunken border border-line rounded-lg p-3">
            <summary className="text-xs uppercase tracking-wide text-ink-muted cursor-pointer">Dados da Nota Fiscal (opcional)</summary>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input label="Numero da Nota" value={numeroNota} onChange={setNumeroNota} />
              <Input label="Serie" value={serieNota} onChange={setSerieNota} />
              <Input label="Chave NFe (44 dig.)" value={chaveNfe} onChange={setChaveNfe} />
              <Input label="Fornecedor (nome)" value={fornecedorNome} onChange={setFornecedorNome} />
              <Input label="CNPJ fornecedor" value={fornecedorCnpj} onChange={setFornecedorCnpj} />
              <Input label="Observacao" value={observacao} onChange={setObservacao} />
            </div>
          </details>

          <div className="flex gap-2">
            <button
              type="submit" disabled={submitting || !product}
              className="px-5 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              {submitting ? 'Lancando...' : 'Lancar movimento'}
            </button>
            <button type="button" onClick={() => resetForm()}
              className="px-4 py-2 bg-sunken hover:bg-line text-ink-muted text-sm rounded-lg">
              Limpar
            </button>
          </div>
        </form>
      </section>

      <section className="bg-surface border border-line rounded-xl p-6">
        <h2 className="text-lg font-semibold text-ink mb-3">Ultimos lancamentos</h2>
        {loading ? <p className="text-sm text-ink-faint">Carregando...</p>
         : error ? <Alert type="error" message={error} />
         : movements.length === 0 ? <p className="text-sm text-ink-faint">Nenhum movimento ainda.</p>
         : (
          <div className="overflow-y-auto max-h-[500px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface">
                <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                  <th className="pb-2 font-medium">Data</th>
                  <th className="pb-2 font-medium">Produto</th>
                  <th className="pb-2 font-medium">SKU</th>
                  <th className="pb-2 font-medium">Tipo</th>
                  <th className="pb-2 font-medium text-right">Qtd</th>
                  <th className="pb-2 font-medium text-right">Valor unit.</th>
                  <th className="pb-2 font-medium pr-3">NF</th>
                </tr>
              </thead>
              <tbody>
                {movements.map(m => (
                  <tr key={m.id} className="border-b border-line last:border-0">
                    <td className="py-1.5 pr-3 text-ink-muted">{fmtDateTime(m.created_at)}</td>
                    <td className="py-1.5 pr-3 text-ink-muted max-w-[280px] truncate" title={m.products?.name ?? ''}>{m.products?.name ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-ink-muted">{m.products?.sku ?? '—'}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        m.tipo === 'ENTRADA' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30'
                        : m.tipo === 'SAIDA' ? 'bg-danger/10 text-danger border border-danger/30'
                        : 'bg-warn/15 text-[#9a5a28] border border-warn/40 dark:bg-warn/20 dark:text-amber-300'
                      }`}>{m.tipo}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-ink-muted">{m.quantidade}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-ink-muted">{fmtBRL(m.valor_unitario)}</td>
                    <td className="py-1.5 pr-3 text-ink-muted">{m.numero_nota ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </section>
    </div>
  )
}

// =====================================================================
// Aba: CADASTRO DE PRODUTO
// =====================================================================

function CadastroProdutoTab() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { searchProducts } = useStockMovements(0)
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({
    sku: '', barcode: '', name: '', supplier_id: '', unit: 'UN',
    cost: '', sale_price: '',
    ncm: '', cest: '', cfop: '', cst_csosn: '',
    aliquota_icms: '0', aliquota_pis: '0', aliquota_cofins: '0',
    is_active: true,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Product[]>([])
  const [searching, setSearching] = useState(false)
  const [msg, setMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const [{ data: profile }, supRes] = await Promise.all([
        supabase.from('profiles').select('company_id').eq('id', user.id).single(),
        supabase.from('suppliers').select('*').order('name'),
      ])
      setCompanyId((profile?.company_id as string | undefined) ?? null)
      setSuppliers((supRes.data ?? []) as Supplier[])
    })()
  }, [])

  // Pre-preenche o barcode quando navegado a partir do Recebimento via ?barcode=...
  useEffect(() => {
    const initialBarcode = searchParams.get('barcode')
    if (initialBarcode) {
      setForm(f => ({ ...f, barcode: initialBarcode }))
      setMsg({ type: 'success', text: `Codigo "${initialBarcode}" pre-preenchido. Busque o produto pelo SKU ou nome abaixo para associar e salvar.` })
      // Limpa o querystring para evitar re-preencher em re-renders
      const next = new URLSearchParams(searchParams)
      next.delete('barcode')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function reset() {
    setEditId(null)
    setForm({
      sku: '', barcode: '', name: '', supplier_id: '', unit: 'UN',
      cost: '', sale_price: '',
      ncm: '', cest: '', cfop: '', cst_csosn: '',
      aliquota_icms: '0', aliquota_pis: '0', aliquota_cofins: '0',
      is_active: true,
    })
    setSearchQuery('')
    setSearchResults([])
  }

  async function runSearch() {
    const q = searchQuery.trim()
    if (!q) { setSearchResults([]); return }
    setSearching(true)
    const res = await searchProducts(q)
    setSearching(false)
    setSearchResults(res)
    if (res.length === 0) setMsg({ type: 'error', text: `Nenhum produto encontrado para "${q}" (busca por SKU ou nome).` })
    else setMsg(null)
  }

  function loadProduct(p: Product) {
    setEditId(p.id)
    // Preserva o barcode pre-preenchido pelo Recebimento (se houver) quando o
    // produto selecionado nao tiver barcode cadastrado.
    const preservedBarcode = !p.barcode && form.barcode ? form.barcode : (p.barcode ?? '')
    setForm({
      sku: p.sku ?? '',
      barcode: preservedBarcode,
      name: p.name ?? '',
      supplier_id: p.supplier_id ?? '',
      unit: p.unit ?? 'UN',
      cost: String(p.cost ?? ''),
      sale_price: String(p.sale_price ?? ''),
      ncm: p.ncm ?? '',
      cest: p.cest ?? '',
      cfop: p.cfop ?? '',
      cst_csosn: p.cst_csosn ?? '',
      aliquota_icms: String(p.aliquota_icms ?? '0'),
      aliquota_pis: String(p.aliquota_pis ?? '0'),
      aliquota_cofins: String(p.aliquota_cofins ?? '0'),
      is_active: p.is_active ?? true,
    })
    setMsg(null)
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!companyId) { setMsg({ type: 'error', text: 'Empresa nao carregada.' }); return }
    if (!form.sku.trim() || !form.name.trim()) {
      setMsg({ type: 'error', text: 'SKU e descricao sao obrigatorios.' }); return
    }
    setSubmitting(true)
    const payload = {
      company_id: companyId,
      sku: form.sku.trim(),
      name: form.name.trim(),
      barcode: form.barcode.trim() || null,
      supplier_id: form.supplier_id || null,
      unit: form.unit.trim() || 'UN',
      cost: Number(form.cost.replace(',', '.')) || 0,
      sale_price: form.sale_price ? Number(form.sale_price.replace(',', '.')) : null,
      ncm: form.ncm.trim() || null,
      cest: form.cest.trim() || null,
      cfop: form.cfop.trim() || null,
      cst_csosn: form.cst_csosn.trim() || null,
      aliquota_icms: Number(form.aliquota_icms.replace(',', '.')) || 0,
      aliquota_pis: Number(form.aliquota_pis.replace(',', '.')) || 0,
      aliquota_cofins: Number(form.aliquota_cofins.replace(',', '.')) || 0,
      is_active: form.is_active,
      source: 'manual' as const,
    }
    const op = editId
      ? supabase.from('products').update(payload).eq('id', editId)
      : supabase.from('products').insert(payload)
    const { error } = await op
    setSubmitting(false)
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setMsg({ type: 'success', text: editId ? 'Produto atualizado.' : 'Produto cadastrado.' })
    reset()
  }

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm(f => ({ ...f, [k]: v }))

  return (
    <section className="bg-surface border border-line rounded-xl p-6">
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">Buscar produto existente (SKU ou nome)</label>
            <input
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runSearch() } }}
              placeholder="Digite parte do SKU ou da descricao + Enter"
              className="w-full bg-paper border border-line rounded px-3 py-2 text-ink text-sm"
            />
          </div>
          <button type="button" onClick={runSearch} disabled={searching}
            className="px-4 py-2 bg-sunken hover:bg-line disabled:opacity-50 text-ink-muted text-sm rounded-lg">
            {searching ? 'Buscando...' : 'Buscar'}
          </button>
          <button type="button" onClick={reset}
            className="px-4 py-2 bg-sunken hover:bg-line text-ink-muted text-sm rounded-lg">Novo</button>
        </div>

        {searchResults.length > 0 && (
          <div className="rounded-lg border border-line bg-sunken max-h-[260px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface">
                <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                  <th className="px-3 py-2 font-medium">SKU</th>
                  <th className="px-3 py-2 font-medium">Descricao</th>
                  <th className="px-3 py-2 font-medium">GTIN/EAN</th>
                  <th className="px-3 py-2 font-medium text-right">Saldo</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map(p => (
                  <tr key={p.id} className={`border-b border-line last:border-0 ${p.is_active === false ? 'opacity-60' : ''}`}>
                    <td className="px-3 py-1.5 text-ink-muted">{p.sku}</td>
                    <td className="px-3 py-1.5 text-ink-muted max-w-[320px] truncate" title={p.name}>{p.name}</td>
                    <td className="px-3 py-1.5 text-ink-faint">{p.barcode ?? <span className="text-warn">vazio</span>}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-ink-muted">{p.current_stock}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button type="button" onClick={() => loadProduct(p)}
                        className="px-2 py-1 bg-brand-500 hover:bg-brand-600 text-white text-[11px] rounded">
                        Selecionar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} /></div>}

      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-3 text-xs uppercase tracking-wide text-ink-faint">Identificacao</div>
        <Input label="SKU *" value={form.sku} onChange={v => set('sku', v)} />
        <Input label="Codigo de barras" value={form.barcode} onChange={v => set('barcode', v)} />
        <Input label="Unidade" value={form.unit} onChange={v => set('unit', v)} />
        <div className="md:col-span-2">
          <Input label="Descricao *" value={form.name} onChange={v => set('name', v)} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">Fornecedor padrao</label>
          <select value={form.supplier_id} onChange={e => set('supplier_id', e.target.value)}
            className="w-full bg-paper border border-line rounded px-2 py-2 text-ink text-sm">
            <option value="">— sem fornecedor —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="md:col-span-3 text-xs uppercase tracking-wide text-ink-faint mt-2">Precos</div>
        <Input label="Preco de custo (R$)" value={form.cost} onChange={v => set('cost', v)} />
        <Input label="Preco de venda (R$)" value={form.sale_price} onChange={v => set('sale_price', v)} />
        <div />

        <div className="md:col-span-3 text-xs uppercase tracking-wide text-ink-faint mt-2">Fiscal</div>
        <Input label="NCM" value={form.ncm} onChange={v => set('ncm', v)} />
        <Input label="CEST" value={form.cest} onChange={v => set('cest', v)} />
        <Input label="CFOP" value={form.cfop} onChange={v => set('cfop', v)} />
        <Input label="CST / CSOSN" value={form.cst_csosn} onChange={v => set('cst_csosn', v)} />
        <Input label="Aliquota ICMS (%)" value={form.aliquota_icms} onChange={v => set('aliquota_icms', v)} />
        <Input label="Aliquota PIS (%)" value={form.aliquota_pis} onChange={v => set('aliquota_pis', v)} />
        <Input label="Aliquota COFINS (%)" value={form.aliquota_cofins} onChange={v => set('aliquota_cofins', v)} />

        <div className="md:col-span-3 flex items-center gap-2 mt-2">
          <input type="checkbox" id="ativo" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
          <label htmlFor="ativo" className="text-sm text-ink-muted">Ativo</label>
        </div>

        <div className="md:col-span-3 flex gap-2">
          <button type="submit" disabled={submitting}
            className="px-5 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
            {submitting ? 'Salvando...' : editId ? 'Atualizar produto' : 'Cadastrar produto'}
          </button>
        </div>
      </form>
    </section>
  )
}

// =====================================================================
// Aba: IMPORTAR XML NFe
// =====================================================================

interface XmlItemRow extends NfeItem {
  matched: Product | null
  enabled: boolean
  bipado_qty: number
}

function ImportarXmlTab() {
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [parsed, setParsed] = useState<NfeParsed | null>(null)
  const [rows, setRows] = useState<XmlItemRow[]>([])
  const [supplierMatch, setSupplierMatch] = useState<Supplier | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [msg, setMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)
  const [scanInput, setScanInput] = useState('')
  const [scanFeedback, setScanFeedback] = useState<{ type: 'ok' | 'warn' | 'err'; text: string } | null>(null)
  const [highlightIdx, setHighlightIdx] = useState<number | null>(null)
  const scanRef = useRef<HTMLInputElement>(null)
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([])

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
      setCompanyId((profile?.company_id as string | undefined) ?? null)
    })()
  }, [])

  async function handleFile(file: File) {
    setMsg(null); setParsed(null); setRows([]); setSupplierMatch(null)
    try {
      const text = await file.text()
      const nfe = parseNfeXml(text)
      setParsed(nfe)

      // Tenta casar fornecedor pelo CNPJ
      if (nfe.fornecedor.cnpj) {
        const { data: sup } = await supabase
          .from('suppliers').select('*').eq('cnpj', nfe.fornecedor.cnpj).maybeSingle()
        if (sup) setSupplierMatch(sup as Supplier)
      }

      // Tenta casar produtos por barcode (cEAN) e depois por sku (cProd)
      const matched: XmlItemRow[] = await Promise.all(nfe.itens.map(async item => {
        let p: Product | null = null
        if (item.cEAN) {
          const { data } = await supabase.from('products').select('*').eq('barcode', item.cEAN).is('deleted_at', null).maybeSingle()
          if (data) p = data as Product
        }
        if (!p && item.cProd) {
          const { data } = await supabase.from('products').select('*').eq('sku', item.cProd).is('deleted_at', null).maybeSingle()
          if (data) p = data as Product
        }
        return { ...item, matched: p, enabled: true, bipado_qty: 0 }
      }))
      setRows(matched)
      setScanFeedback(null)
      setHighlightIdx(null)
      setTimeout(() => scanRef.current?.focus(), 100)
    } catch (e) {
      setMsg({ type: 'error', text: (e as Error).message })
    }
  }

  function processScan() {
    const code = scanInput.trim()
    if (!code) return
    setScanInput('')
    // Procura linha cujo cEAN bate
    const idx = rows.findIndex(r => r.cEAN === code)
    if (idx < 0) {
      setScanFeedback({ type: 'err', text: `Codigo "${code}" nao esta nesta nota.` })
      setHighlightIdx(null)
      return
    }
    const row = rows[idx]
    if (row.bipado_qty >= row.quantidade) {
      setScanFeedback({ type: 'warn', text: `${row.descricao}: ja bipou todas as ${row.quantidade} ${row.unidade}.` })
    } else {
      setRows(rs => rs.map((r, i) => i === idx ? { ...r, bipado_qty: r.bipado_qty + 1 } : r))
      setScanFeedback({ type: 'ok', text: `${row.descricao}: ${row.bipado_qty + 1}/${row.quantidade}` })
    }
    setHighlightIdx(idx)
    rowRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => setHighlightIdx(null), 1200)
  }

  const totalQty = rows.reduce((s, r) => s + r.quantidade, 0)
  const bipadoQty = rows.reduce((s, r) => s + r.bipado_qty, 0)
  const itensComBip = rows.filter(r => r.bipado_qty > 0).length

  /**
   * mode = 'xml': entrada com a quantidade que veio no XML (todos enabled).
   * mode = 'bipado': entrada apenas dos itens bipados, com qty = bipado_qty
   *                  (recebimento parcial, conferido fisicamente).
   */
  async function confirm(mode: 'xml' | 'bipado' = 'xml') {
    if (!parsed || !companyId) return
    setSubmitting(true)
    setMsg(null)
    try {
      // 1) Garante fornecedor
      let supplierId: string | null = supplierMatch?.id ?? null
      if (!supplierId && parsed.fornecedor.cnpj) {
        const { data, error } = await supabase.from('suppliers').insert({
          company_id: companyId,
          name: parsed.fornecedor.nome,
          cnpj: parsed.fornecedor.cnpj,
        }).select('id').single()
        if (error) throw new Error(`Fornecedor: ${error.message}`)
        supplierId = (data?.id as string) ?? null
      }

      // 2) Seleciona linhas conforme o modo
      const selectedRows = mode === 'bipado'
        ? rows.filter(r => r.bipado_qty > 0)
        : rows.filter(r => r.enabled)
      if (selectedRows.length === 0) {
        throw new Error(mode === 'bipado' ? 'Nenhum item foi bipado.' : 'Nenhum item selecionado.')
      }

      // 3) Cria produtos faltantes (mantem matched para os existentes)
      const movementsPayload: Array<{ row: XmlItemRow; productId: string; qty: number }> = []
      for (const r of selectedRows) {
        let productId: string | null = r.matched?.id ?? null
        if (!productId) {
          const { data, error } = await supabase.from('products').insert({
            company_id: companyId,
            sku: r.cProd || (r.cEAN ?? r.descricao.slice(0, 30)),
            name: r.descricao,
            barcode: r.cEAN,
            unit: r.unidade || 'UN',
            cost: r.valor_unitario,
            ncm: r.ncm || null,
            cfop: r.cfop || null,
            cst_csosn: r.cst_csosn,
            supplier_id: supplierId,
            is_active: true,
            current_stock: 0,
            source: 'manual',
          }).select('id').single()
          if (error) throw new Error(`Produto "${r.descricao}": ${error.message}`)
          productId = data!.id as string
        }
        const qty = mode === 'bipado' ? r.bipado_qty : r.quantidade
        movementsPayload.push({ row: r, productId: productId!, qty })
      }

      // 4) Cria movimentos de entrada
      const { data: { user } } = await supabase.auth.getUser()
      const movementsInsert = movementsPayload.map(({ row, productId, qty }) => ({
        company_id: companyId,
        product_id: productId,
        tipo: 'ENTRADA' as const,
        quantidade: qty,
        valor_unitario: row.valor_unitario,
        numero_nota: parsed.numero,
        serie_nota: parsed.serie,
        chave_nfe: parsed.chave,
        supplier_id: supplierId,
        fornecedor_nome: parsed.fornecedor.nome,
        fornecedor_cnpj: parsed.fornecedor.cnpj,
        observacao: mode === 'bipado'
          ? `Importado via XML NFe (conferido por bipagem${qty !== row.quantidade ? ` - parcial ${qty}/${row.quantidade}` : ''})`
          : 'Importado via XML NFe',
        created_by: user?.id ?? null,
      }))
      if (movementsInsert.length > 0) {
        const { error } = await supabase.from('stock_movements').insert(movementsInsert)
        if (error) throw new Error(`Movimentos: ${error.message}`)
      }

      setMsg({
        type: 'success',
        text: `Importacao concluida: ${movementsPayload.length} itens registrados${mode === 'bipado' ? ' (somente bipados)' : ''}.`,
      })
      setParsed(null); setRows([]); setSupplierMatch(null); setScanFeedback(null)
    } catch (e) {
      setMsg({ type: 'error', text: (e as Error).message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="bg-surface border border-line rounded-xl p-6 flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-ink">Importar XML da NFe</h2>
      <p className="text-sm text-ink-faint">
        Selecione o XML da nota — o sistema preenche fornecedor, chave, NCM, CFOP, CST/CSOSN, qtd e valor unitario.
        Produtos faltantes sao criados automaticamente (com os dados fiscais do XML) e o saldo de cada item recebe uma ENTRADA.
      </p>

      <input
        type="file" accept=".xml,application/xml,text/xml"
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
        className="block w-full text-sm text-ink-muted file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-brand-500 file:text-white file:cursor-pointer hover:file:bg-brand-600"
      />

      {msg && <Alert type={msg.type} message={msg.text} />}

      {parsed && (
        <>
          <div className="rounded-lg border border-line bg-sunken p-3 text-sm grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><span className="text-ink-faint">Fornecedor:</span> <span className="text-ink">{parsed.fornecedor.nome}</span></div>
            <div><span className="text-ink-faint">CNPJ:</span> <span className="text-ink">{parsed.fornecedor.cnpj}</span> {supplierMatch && <span className="text-emerald-600">(cadastrado)</span>}</div>
            <div><span className="text-ink-faint">Nota:</span> <span className="text-ink">{parsed.numero}/{parsed.serie}</span></div>
            <div className="truncate"><span className="text-ink-faint">Chave:</span> <span className="text-ink">{parsed.chave}</span></div>
          </div>

          {/* Conferencia por bipagem */}
          <div className="rounded-lg border border-line bg-sunken p-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">Bipar item recebido</label>
              <input
                ref={scanRef}
                value={scanInput}
                onChange={e => setScanInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); processScan() } }}
                placeholder="Bipe o codigo de barras do item fisico (+ Enter)"
                className="w-full bg-paper border border-line rounded px-3 py-2 text-ink text-sm"
              />
            </div>
            <div className="text-sm text-ink-muted tabular-nums">
              <span className="text-ink-faint">Conferidos:</span> {itensComBip}/{rows.length} itens · {bipadoQty}/{totalQty} un.
            </div>
            <button type="button" onClick={() => setRows(rs => rs.map(r => ({ ...r, bipado_qty: 0 })))}
              className="px-3 py-1.5 bg-sunken hover:bg-line text-ink-muted text-xs rounded-lg">
              Zerar bipagem
            </button>
          </div>

          {scanFeedback && (
            <div className={`text-xs px-3 py-2 rounded ${
              scanFeedback.type === 'ok' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30'
              : scanFeedback.type === 'warn' ? 'bg-warn/15 text-[#9a5a28] border border-warn/40 dark:bg-warn/20 dark:text-amber-300'
              : 'bg-danger/10 text-danger border border-danger/30'
            }`}>
              {scanFeedback.text}
            </div>
          )}

          <div className="overflow-y-auto max-h-[500px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface">
                <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                  <th className="pb-2 pr-2 font-medium">Importar</th>
                  <th className="pb-2 pr-2 font-medium">EAN</th>
                  <th className="pb-2 pr-2 font-medium">SKU (cProd)</th>
                  <th className="pb-2 pr-2 font-medium">Descricao</th>
                  <th className="pb-2 pr-2 font-medium">NCM/CFOP</th>
                  <th className="pb-2 pr-2 font-medium text-right">Qtd XML</th>
                  <th className="pb-2 pr-2 font-medium text-right">Bipado</th>
                  <th className="pb-2 pr-2 font-medium text-right">V. unit.</th>
                  <th className="pb-2 pr-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const completo = r.bipado_qty === r.quantidade && r.quantidade > 0
                  const parcial = r.bipado_qty > 0 && r.bipado_qty < r.quantidade
                  return (
                  <tr
                    key={i}
                    ref={el => { rowRefs.current[i] = el }}
                    className={`border-b border-line last:border-0 transition-colors ${
                      highlightIdx === i ? 'bg-emerald-100 dark:bg-emerald-500/15' : ''
                    }`}
                  >
                    <td className="py-1.5 pr-2">
                      <input type="checkbox" checked={r.enabled}
                        onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))} />
                    </td>
                    <td className="py-1.5 pr-2 text-ink-faint">{r.cEAN ?? '—'}</td>
                    <td className="py-1.5 pr-2 text-ink-faint">{r.cProd}</td>
                    <td className="py-1.5 pr-2 text-ink-muted max-w-[280px] truncate" title={r.descricao}>{r.descricao}</td>
                    <td className="py-1.5 pr-2 text-ink-faint">{r.ncm}/{r.cfop}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-ink-muted">{r.quantidade}</td>
                    <td className={`py-1.5 pr-2 text-right tabular-nums font-semibold ${
                      completo ? 'text-emerald-600' : parcial ? 'text-warn' : 'text-ink-faint'
                    }`}>
                      {r.bipado_qty}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-ink-muted">{fmtBRL(r.valor_unitario)}</td>
                    <td className="py-1.5 pr-3">
                      {r.matched
                        ? <span className="text-emerald-600 text-[11px]">cadastrado</span>
                        : <span className="text-warn text-[11px]">sera criado</span>}
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => confirm('xml')} disabled={submitting || rows.length === 0}
              className="px-5 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {submitting ? 'Importando...' : `Confirmar tudo do XML (${rows.filter(r => r.enabled).length} itens)`}
            </button>
            <button type="button" onClick={() => confirm('bipado')} disabled={submitting || itensComBip === 0}
              className="px-5 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {submitting ? 'Importando...' : `Confirmar somente bipados (${itensComBip} itens · ${bipadoQty} un.)`}
            </button>
            <button type="button" onClick={() => { setParsed(null); setRows([]); setSupplierMatch(null); setMsg(null); setScanFeedback(null) }}
              className="px-4 py-2 bg-sunken hover:bg-line text-ink-muted text-sm rounded-lg">
              Cancelar
            </button>
          </div>
        </>
      )}
    </section>
  )
}

// =====================================================================
// Helper de input padronizado
// =====================================================================

interface InputProps {
  label: string
  value: string
  onChange: (v: string) => void
}
function Input({ label, value, onChange }: InputProps) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-ink-faint mb-1">{label}</label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-paper border border-line rounded px-2 py-2 text-ink text-sm"
      />
    </div>
  )
}
